import { defineNuxtPlugin, useRoute, useRuntimeConfig } from '#imports'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'

import { getUserLoadingStrategy } from '../utils/userLoadingStrategy'

/**
 * This plugin handles the initial user load in the application.
 * Depending on the app and route configuration, the user may be fetched server-side or client-side.
 * Fetching the user client-side prevents cache pollution (a user seeing another user's data).
 * More info on how to use it in details in the README.md
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  const config = useRuntimeConfig()

  if (
    process.env.VITEST === 'true' &&
    process.env.IS_USER_PLUGIN_TEST !== 'true'
  )
    return

  const appUserLoadingStrategy = config.public.oauth.userLoadingStrategy

  const route = useRoute()

  const userLoadingStrategy = getUserLoadingStrategy(
    route,
    appUserLoadingStrategy,
  )

  if (userLoadingStrategy === 'never') return

  const { isUserFetched, prefetchUser, fetchUser } = useUserStore()

  if (isUserFetched.value) return

  const fetchUserAndNotify = async (
    prefetchPromise?: ReturnType<typeof prefetchUser>,
  ) => {
    await fetchUser(prefetchPromise)

    // Notify apps that user has been fetched
    await nuxtApp.callHook('oauth:user-fetched')
  }

  if (process.server && userLoadingStrategy === 'server') {
    await fetchUserAndNotify()

    return
  }

  if (process.client && userLoadingStrategy === 'after-hydration') {
    const prefetchPromise = nuxtApp.isHydrating ? prefetchUser() : undefined

    nuxtApp.hook('app:suspense:resolve', async () => {
      await fetchUserAndNotify(prefetchPromise)
    })
  }
})
